import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useMemo, useRef } from 'react';
import { patchStudent, getStudentDetailComplete, getDocumentosEstudiante, subirFotoEstudiante } from '../../../api/students';
import styles from '../../../styles/EstudiantesStyles/Estudiantes.module.css';

const FichaMedica = forwardRef(function FichaMedica({ student, mode = 'view', onSaved, fotoUrl, onFotoChanged }, ref) {
  // ============ ESTADO DEL FORMULARIO ============
  const buildFormData = useCallback((src) => ({
    // Datos generales (solo lectura)
    matricula: src.matricula || '',
    nombreCompleto: src.nombre_completo || '',
    // Datos médicos generales
    peso: src.fm_peso || '',
    estatura: src.fm_estatura || '',
    tipoSangre: src.fm_tipo_sangre || 'O',
    rh: src.fm_rh || '+',
    // ¿Cuenta con servicio médico? '' = no respondido, 'SI' | 'NO'
    tieneServicioMedico: src.fm_tiene_servicio_medico === true
      ? 'SI'
      : src.fm_tiene_servicio_medico === false
      ? 'NO'
      : '',
    servicioMedico: src.fm_servicio_medico || '',
    numeroAfiliacion: src.fm_numero_afiliacion || '',
    fechaSeguro: src.fm_fecha_seguro || '',
    numeroClinica: src.fm_numero_clinica || '',
    // Vacunas
    cartillaCompleta: src.fm_cartilla_completa === true || src.fm_cartilla_completa === 1,
    vacunasPendientes: src.fm_vacunas_pendientes || '',
    // Antecedentes Familiares
    antecedentes_familiares: {
      cancer: false,
      tumoraciones: false,
      anemia: false,
      obesidad: false,
      epilepsia: false,
      problemas_auditivos: false,
      diabetes: false,
      hipertension: false,
      fiebre_reumatica: false,
      migrana: false,
      problemas_visuales: false,
      ...(src.fm_antecedentes_familiares || {})
    },
    // Antecedentes Personales
    antecedentes_personales: {
      hepatitis: false,
      varicela: false,
      tosferina: false,
      parasitosis: false,
      paperas: false,
      neumonia: false,
      sarampion: false,
      poliomielitis: false,
      artritis: false,
      crisis_convulsiva: false,
      tifoideas: false,
      ...(src.fm_antecedentes_personales || {})
    },
    // Fracturas e intervenciones
    fracturas: src.fm_fracturas || '',
    intervenciones_quirurgicas: src.fm_intervenciones_quirurgicas || '',
    // Problemas de salud
    problemas: {
      asma: false,
      problemas_corazon: false,
      auditivos: false,
      migrana: false,
      visuales: false,
      enfermedades_contagiosas: false,
      diabetes: false,
      respiratorios: false,
      ...(src.fm_problemas_salud || {})
    },
    // Reacciones alérgicas
    reacciones_alergicas: {
      medicamento: '',
      plantas: '',
      padecimiento: '',
      tratamiento: '',
      alimento: '',
      otros: '',
      ...(src.fm_reacciones_alergicas || {})
    },
    // Observaciones
    observaciones_importantes: src.fm_observaciones_medicas || '',
    // Datos del médico
    medico: {
      nombre: src.fm_medico_nombre || '',
      domicilio: src.fm_medico_domicilio || '',
      telefonos: src.fm_medico_telefonos || '',
      clinica_preferencia: src.fm_medico_clinica_preferencia || ''
    },
    // Contactos de emergencia
    emergencia_papa: {
      nombre: src.fm_emergencia_papa_nombre || '',
      telefono: src.fm_emergencia_papa_telefono || ''
    },
    emergencia_mama: {
      nombre: src.fm_emergencia_mama_nombre || '',
      telefono: src.fm_emergencia_mama_telefono || ''
    },
    emergencia_otro: {
      nombre: src.fm_emergencia_otro_nombre || '',
      telefono: src.fm_emergencia_otro_telefono || '',
      parentesco: src.fm_emergencia_otro_parentesco || ''
    }
  }), []);

  const [formData, setFormData] = useState(() => buildFormData(student));
  const [activeSubTab, setActiveSubTab] = useState('antecedentes');
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(mode === 'edit' || mode === 'create');
  const [hasChanges, setHasChanges] = useState(false);
  const [docs, setDocs] = useState([]);
  const [fotoUrlLocal, setFotoUrlLocal] = useState('');
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const fotoInputRef = useRef(null);

  // Exponer getFormPayload al padre via ref
  useImperativeHandle(ref, () => ({
    getFormPayload: () => mapFormToPayload(),
  }));

  // Recargar datos si cambia el estudiante
  useEffect(() => {
    setFormData(buildFormData(student));
    setHasChanges(false);
  }, [student, buildFormData]);

  useEffect(() => {
    const loadDocumentos = async () => {
      const studentId = student?.id_estudiante || student?.id;
      if (!studentId) {
        setDocs([]);
        return;
      }
      try {
        const data = await getDocumentosEstudiante(studentId);
        const list = Array.isArray(data) ? data : (data?.results || []);
        setDocs(Array.isArray(list) ? list : []);
      } catch {
        setDocs([]);
      }
    };

    loadDocumentos();
  }, [student?.id_estudiante, student?.id]);

  const getApiBase = useCallback(() => {
    const envBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
    if (envBase) return envBase;
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/api`;
    }
    return '';
  }, []);

  const fotoPerfilDoc = useMemo(() => {
    const list = Array.isArray(docs) && docs.length > 0
      ? docs
      : (Array.isArray(student?.documentos) ? student.documentos : []);
    return list.find((d) => d.tipo_documento === 'FOTO_PERFIL') || list.find((d) => d.tipo_documento === 'PROFILE');
  }, [docs, student]);

  const fotoPerfilUrl = useMemo(() => {
    if (fotoUrl) return fotoUrl;
    if (fotoUrlLocal) return fotoUrlLocal;
    if (fotoPerfilDoc?.archivo_url) return fotoPerfilDoc.archivo_url;
    if (fotoPerfilDoc?.url) {
      const mediaBase = getApiBase().replace(/\/api$/, '');
      return `${mediaBase}${fotoPerfilDoc.url}`;
    }
    if (fotoPerfilDoc?.ruta_archivo) {
      const mediaBase = getApiBase().replace(/\/api$/, '');
      return `${mediaBase}/media/${fotoPerfilDoc.ruta_archivo}`;
    }
    return '';
  }, [fotoPerfilDoc, fotoUrlLocal, fotoUrl, getApiBase]);

  const handleSelectFotoClick = () => {
    if (fotoInputRef.current) fotoInputRef.current.click();
  };

  const handleFotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const studentId = student?.id_estudiante || student?.id;
    const name = (file.name || '').toLowerCase();
    const isImage = /^image\//.test(file.type) || ['.jpg', '.jpeg', '.png', '.webp'].some((ext) => name.endsWith(ext));
    if (!isImage) {
      alert('Solo se permiten imágenes JPG, PNG o WEBP para la foto de perfil.');
      event.target.value = '';
      return;
    }
    if (!studentId) {
      alert('No se encontró el identificador del estudiante.');
      event.target.value = '';
      return;
    }

    setUploadingFoto(true);
    try {
      const resp = await subirFotoEstudiante(studentId, file);
      const uploadedUrl = resp?.archivo_url || '';
      setFotoUrlLocal(uploadedUrl || URL.createObjectURL(file));
      if (typeof onFotoChanged === 'function') {
        await onFotoChanged();
      }
      alert('Foto de perfil guardada correctamente.');
    } catch (err) {
      console.error('Error al subir foto de perfil:', err);
      alert(`No se pudo subir la foto: ${err?.response?.data?.error || err.message}`);
    } finally {
      setUploadingFoto(false);
      event.target.value = '';
    }
  };

  // ============ HANDLERS ============
  const handleInputChange = (field, value) => {
    if (!isEditing) return;
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleNestedChange = (parent, field, value) => {
    if (!isEditing) return;
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  // ============ MAPEO FRONTEND -> BACKEND ============
  const mapFormToPayload = () => {
    return {
      fm_peso: formData.peso || null,
      fm_estatura: formData.estatura || null,
      fm_tipo_sangre: formData.tipoSangre || null,
      fm_rh: formData.rh || null,
      // Booleano explícito: 'SI' -> true, 'NO' -> false, '' -> null (no respondido)
      fm_tiene_servicio_medico:
        formData.tieneServicioMedico === 'SI'
          ? true
          : formData.tieneServicioMedico === 'NO'
          ? false
          : null,
      // Si NO tiene seguro, los campos asociados se limpian
      fm_servicio_medico: formData.tieneServicioMedico === 'NO' ? null : (formData.servicioMedico || null),
      fm_numero_afiliacion: formData.tieneServicioMedico === 'NO' ? null : (formData.numeroAfiliacion || null),
      fm_fecha_seguro: formData.tieneServicioMedico === 'NO' ? null : (formData.fechaSeguro || null),
      fm_numero_clinica: formData.tieneServicioMedico === 'NO' ? null : (formData.numeroClinica || null),
      fm_cartilla_completa: formData.cartillaCompleta,
      fm_vacunas_pendientes: formData.vacunasPendientes || null,
      fm_antecedentes_familiares: formData.antecedentes_familiares,
      fm_antecedentes_personales: formData.antecedentes_personales,
      fm_fracturas: formData.fracturas || null,
      fm_intervenciones_quirurgicas: formData.intervenciones_quirurgicas || null,
      fm_problemas_salud: formData.problemas,
      fm_reacciones_alergicas: formData.reacciones_alergicas,
      fm_observaciones_medicas: formData.observaciones_importantes || null,
      fm_medico_nombre: formData.medico.nombre || null,
      fm_medico_domicilio: formData.medico.domicilio || null,
      fm_medico_telefonos: formData.medico.telefonos || null,
      fm_medico_clinica_preferencia: formData.medico.clinica_preferencia || null,
      fm_emergencia_papa_nombre: formData.emergencia_papa.nombre || null,
      fm_emergencia_papa_telefono: formData.emergencia_papa.telefono || null,
      fm_emergencia_mama_nombre: formData.emergencia_mama.nombre || null,
      fm_emergencia_mama_telefono: formData.emergencia_mama.telefono || null,
      fm_emergencia_otro_nombre: formData.emergencia_otro.nombre || null,
      fm_emergencia_otro_telefono: formData.emergencia_otro.telefono || null,
      fm_emergencia_otro_parentesco: formData.emergencia_otro.parentesco || null,
    };
  };

  // ============ GUARDAR ============
  const handleSave = async () => {
    const studentId = student.id_estudiante || student.id;
    if (!studentId) {
      alert('Error: No se puede identificar al estudiante.');
      return;
    }
    setSaving(true);
    try {
      const payload = mapFormToPayload();
      await patchStudent(studentId, payload);
      // Re-fetch student data to get the updated fm_ fields from server
      const updatedStudent = await getStudentDetailComplete(studentId);
      // Update local form with fresh server data
      setFormData(buildFormData(updatedStudent));
      setHasChanges(false);
      setIsEditing(false);
      alert('Ficha medica guardada exitosamente.');
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error al guardar ficha medica:', err);
      let msg = 'Error desconocido al guardar.';
      if (err?.response?.data) {
        if (typeof err.response.data === 'string') {
          msg = err.response.data;
        } else if (typeof err.response.data === 'object') {
          msg = Object.entries(err.response.data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('\n');
        }
      }
      alert('Error al guardar ficha medica:\n' + msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(buildFormData(student));
    setHasChanges(false);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const subTabs = [
    { id: 'antecedentes', label: 'Antecedentes' },
    { id: 'enfermedades', label: 'Enfermedades' },
    { id: 'contacto', label: 'Contacto' }
  ];

  // Helper: renderizar input con soporte modo ver/editar
  const renderInput = (props) => {
    const { value, onChange, disabled, type = 'text', placeholder, ...rest } = props;
    return (
      <input
        type={type}
        className={styles.fichaMedicaInput}
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled || !isEditing}
        placeholder={isEditing ? placeholder : ''}
        {...rest}
      />
    );
  };

  return (
    <div className={styles.fichaMedicaContainer}>
      {/* Header con datos generales */}
      <div className={styles.fichaMedicaHeader}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>Ficha Medica</span>
          {!isEditing && (
            <button
              onClick={handleEdit}
              style={{
                padding: '6px 16px',
                backgroundColor: '#4A90D9',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Editar
            </button>
          )}
        </div>
        <div className={styles.fichaMedicaHeaderGrid}>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>Matricula</label>
            {renderInput({ value: formData.matricula, disabled: true })}
          </div>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>Ciclo escolar Seccion</label>
            {renderInput({
              value: `${student.ciclo_escolar_nombre || student.ciclo_escolar || ''} - ${student.seccion_nombre || student.seccion || ''}`,
              disabled: true
            })}
          </div>
          <div className={styles.fichaMedicaFotoContainer}>
            <div
              className={styles.fichaMedicaFoto}
              onClick={handleSelectFotoClick}
              style={{ cursor: uploadingFoto ? 'not-allowed' : 'pointer' }}
              title="Cambiar imagen de perfil"
            >
              {fotoPerfilUrl ? (
                <img
                  src={fotoPerfilUrl}
                  alt="Foto de perfil"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span>(FOTO)</span>
              )}
            </div>
            <input
              ref={fotoInputRef}
              type="file"
              accept="image/*,.jpg,.jpeg,.png,.webp"
              style={{ display: 'none' }}
              onChange={handleFotoChange}
            />
            <button
              type="button"
              className={styles.expedienteActionButton}
              onClick={handleSelectFotoClick}
              disabled={uploadingFoto}
              style={{ marginTop: 8, width: '100%' }}
            >
              {uploadingFoto ? 'Subiendo...' : 'Seleccionar imagen'}
            </button>
          </div>
        </div>

        <div className={styles.fichaMedicaHeaderGrid}>
          <div className={styles.fichaMedicaField} style={{ gridColumn: 'span 2' }}>
            {renderInput({ value: formData.nombreCompleto, disabled: true })}
          </div>
        </div>

        <div className={styles.fichaMedicaHeaderGrid}>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>Peso (kg)</label>
            {renderInput({
              type: 'text',
              value: formData.peso,
              onChange: (e) => handleInputChange('peso', e.target.value),
              placeholder: 'Ej: 70'
            })}
          </div>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>Estatura (cm)</label>
            {renderInput({
              type: 'text',
              value: formData.estatura,
              onChange: (e) => handleInputChange('estatura', e.target.value),
              placeholder: 'Ej: 170'
            })}
          </div>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>Tipo de sangre</label>
            <select
              className={styles.fichaMedicaSelect}
              value={formData.tipoSangre}
              onChange={(e) => handleInputChange('tipoSangre', e.target.value)}
              disabled={!isEditing}
            >
              <option value="O">O</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="AB">AB</option>
            </select>
          </div>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>RH</label>
            <select
              className={styles.fichaMedicaSelect}
              value={formData.rh}
              onChange={(e) => handleInputChange('rh', e.target.value)}
              disabled={!isEditing}
            >
              <option value="+">+</option>
              <option value="-">-</option>
            </select>
          </div>
        </div>

        <div className={styles.fichaMedicaHeaderGrid}>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>¿Cuenta con seguro contra accidentes?</label>
            <select
              className={styles.fichaMedicaSelect}
              value={formData.tieneServicioMedico}
              onChange={(e) => {
                const val = e.target.value;
                handleInputChange('tieneServicioMedico', val);
                // Al marcar NO, limpiamos automáticamente los campos asociados
                if (val === 'NO') {
                  setFormData(prev => ({
                    ...prev,
                    tieneServicioMedico: 'NO',
                    servicioMedico: '',
                    numeroAfiliacion: '',
                    numeroClinica: '',
                    fechaSeguro: '',
                  }));
                }
              }}
              disabled={!isEditing}
            >
              <option value="">-- Seleccionar --</option>
              <option value="SI">Sí</option>
              <option value="NO">No</option>
            </select>
          </div>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>Servicio medico</label>
            {renderInput({
              value: formData.servicioMedico,
              onChange: (e) => handleInputChange('servicioMedico', e.target.value),
              placeholder: 'Ej: IMSS',
              disabled: !isEditing || formData.tieneServicioMedico === 'NO'
            })}
          </div>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>No. Afiliacion</label>
            {renderInput({
              value: formData.numeroAfiliacion,
              onChange: (e) => handleInputChange('numeroAfiliacion', e.target.value),
              disabled: !isEditing || formData.tieneServicioMedico === 'NO'
            })}
          </div>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>Fecha de seguro</label>
            {renderInput({
              type: 'date',
              value: formData.fechaSeguro,
              onChange: (e) => handleInputChange('fechaSeguro', e.target.value),
              disabled: !isEditing || formData.tieneServicioMedico === 'NO'
            })}
          </div>
          <div className={styles.fichaMedicaField}>
            <label className={styles.fichaMedicaLabel}>No. Clinica</label>
            {renderInput({
              value: formData.numeroClinica,
              onChange: (e) => handleInputChange('numeroClinica', e.target.value),
              disabled: !isEditing || formData.tieneServicioMedico === 'NO'
            })}
          </div>
        </div>

        {/* Vacunas */}
        <div className={styles.fichaMedicaVacunas}>
          <label className={styles.fichaMedicaLabel}>Vacunas:</label>
          <div className={styles.fichaMedicaRadioGroup}>
            <label className={styles.fichaMedicaRadioLabel}>
              <input
                type="radio"
                name="vacunas"
                checked={formData.cartillaCompleta === true}
                onChange={() => {
                  handleInputChange('cartillaCompleta', true);
                }}
                disabled={!isEditing}
              />
              Cartilla completa
            </label>
            <label className={styles.fichaMedicaRadioLabel}>
              <input
                type="radio"
                name="vacunas"
                checked={formData.cartillaCompleta === false}
                onChange={() => {
                  handleInputChange('cartillaCompleta', false);
                }}
                disabled={!isEditing}
              />
              Cartilla incompleta
            </label>
          </div>
          {formData.cartillaCompleta === false && (
            <div className={styles.fichaMedicaField} style={{ marginTop: '8px' }}>
              <label className={styles.fichaMedicaLabel}>Vacunas pendientes:</label>
              {renderInput({
                value: formData.vacunasPendientes,
                onChange: (e) => handleInputChange('vacunasPendientes', e.target.value),
                placeholder: 'Especifique las vacunas pendientes'
              })}
            </div>
          )}
        </div>
      </div>

      {/* Sub Tabs */}
      <div className={styles.fichaMedicaSubTabs}>
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.fichaMedicaSubTab} ${activeSubTab === tab.id ? styles.fichaMedicaSubTabActive : ''}`}
            onClick={() => setActiveSubTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.fichaMedicaTabContent}>
        {activeSubTab === 'antecedentes' && (
          <div className={styles.fichaMedicaAntecedentes}>
            {/* Antecedentes Familiares */}
            <div className={styles.fichaMedicaSection}>
              <h4 className={styles.fichaMedicaSectionTitle}>Familiares</h4>
              <div className={styles.fichaMedicaCheckboxGrid}>
                {[
                  { key: 'cancer', label: 'Cancer' },
                  { key: 'tumoraciones', label: 'Tumoraciones' },
                  { key: 'anemia', label: 'Anemia' },
                  { key: 'obesidad', label: 'Obesidad' },
                  { key: 'epilepsia', label: 'Epilepsia' },
                  { key: 'problemas_auditivos', label: 'Problemas auditivos' },
                  { key: 'diabetes', label: 'Diabetes' },
                  { key: 'hipertension', label: 'Hipertension' },
                  { key: 'fiebre_reumatica', label: 'Fiebre reumatica' },
                  { key: 'migrana', label: 'Migrana' },
                  { key: 'problemas_visuales', label: 'Problemas visuales' },
                ].map(({ key, label }) => (
                  <label key={key} className={styles.fichaMedicaCheckboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.antecedentes_familiares[key] || false}
                      onChange={(e) => handleNestedChange('antecedentes_familiares', key, e.target.checked)}
                      disabled={!isEditing}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Antecedentes Personales */}
            <div className={styles.fichaMedicaSection}>
              <h4 className={styles.fichaMedicaSectionTitle}>Personales</h4>
              <div className={styles.fichaMedicaCheckboxGrid}>
                {[
                  { key: 'hepatitis', label: 'Hepatitis' },
                  { key: 'varicela', label: 'Varicela' },
                  { key: 'tosferina', label: 'Tosferina' },
                  { key: 'parasitosis', label: 'Parasitosis' },
                  { key: 'paperas', label: 'Paperas' },
                  { key: 'neumonia', label: 'Neumonia' },
                  { key: 'sarampion', label: 'Sarampion' },
                  { key: 'poliomielitis', label: 'Poliomielitis' },
                  { key: 'artritis', label: 'Artritis' },
                  { key: 'crisis_convulsiva', label: 'Crisis convulsiva' },
                  { key: 'tifoideas', label: 'Tifoideas' },
                ].map(({ key, label }) => (
                  <label key={key} className={styles.fichaMedicaCheckboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.antecedentes_personales[key] || false}
                      onChange={(e) => handleNestedChange('antecedentes_personales', key, e.target.checked)}
                      disabled={!isEditing}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Fracturas e Intervenciones */}
            <div className={styles.fichaMedicaSection}>
              <div className={styles.fichaMedicaField}>
                <label className={styles.fichaMedicaLabel}>Fracturas:</label>
                <textarea
                  className={styles.fichaMedicaTextarea}
                  value={formData.fracturas}
                  onChange={(e) => handleInputChange('fracturas', e.target.value)}
                  rows="2"
                  placeholder={isEditing ? 'Especifique fracturas si las hay' : ''}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.fichaMedicaField}>
                <label className={styles.fichaMedicaLabel}>Intervenciones quirurgicas:</label>
                <textarea
                  className={styles.fichaMedicaTextarea}
                  value={formData.intervenciones_quirurgicas}
                  onChange={(e) => handleInputChange('intervenciones_quirurgicas', e.target.value)}
                  rows="2"
                  placeholder={isEditing ? 'Especifique intervenciones quirurgicas si las hay' : ''}
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>
        )}

        {activeSubTab === 'enfermedades' && (
          <div className={styles.fichaMedicaEnfermedades}>
            {/* Problemas de salud */}
            <div className={styles.fichaMedicaSection}>
              <h4 className={styles.fichaMedicaSectionTitle}>Indique si padece alguno de los siguientes problemas</h4>
              <div className={styles.fichaMedicaCheckboxGrid}>
                {[
                  { key: 'asma', label: 'Asma' },
                  { key: 'problemas_corazon', label: 'Problemas del corazon' },
                  { key: 'auditivos', label: 'Auditivos' },
                  { key: 'migrana', label: 'Migrana' },
                  { key: 'visuales', label: 'Visuales' },
                  { key: 'enfermedades_contagiosas', label: 'Enfermedades contagiosas' },
                  { key: 'diabetes', label: 'Diabetes' },
                  { key: 'respiratorios', label: 'Respiratorios' },
                ].map(({ key, label }) => (
                  <label key={key} className={styles.fichaMedicaCheckboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.problemas[key] || false}
                      onChange={(e) => handleNestedChange('problemas', key, e.target.checked)}
                      disabled={!isEditing}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Reacciones alergicas */}
            <div className={styles.fichaMedicaSection}>
              <h4 className={styles.fichaMedicaSectionTitle}>Reacciones alergicas</h4>
              <div className={styles.fichaMedicaAlergias}>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Medicamento</label>
                  {renderInput({
                    value: formData.reacciones_alergicas.medicamento,
                    onChange: (e) => handleNestedChange('reacciones_alergicas', 'medicamento', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Plantas</label>
                  {renderInput({
                    value: formData.reacciones_alergicas.plantas,
                    onChange: (e) => handleNestedChange('reacciones_alergicas', 'plantas', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Padecimiento</label>
                  {renderInput({
                    value: formData.reacciones_alergicas.padecimiento,
                    onChange: (e) => handleNestedChange('reacciones_alergicas', 'padecimiento', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Tratamiento</label>
                  {renderInput({
                    value: formData.reacciones_alergicas.tratamiento,
                    onChange: (e) => handleNestedChange('reacciones_alergicas', 'tratamiento', e.target.value)
                  })}
                </div>
              </div>
              <div className={styles.fichaMedicaAlergias} style={{ marginTop: '12px' }}>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Alimento</label>
                  {renderInput({
                    value: formData.reacciones_alergicas.alimento,
                    onChange: (e) => handleNestedChange('reacciones_alergicas', 'alimento', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Otros</label>
                  {renderInput({
                    value: formData.reacciones_alergicas.otros,
                    onChange: (e) => handleNestedChange('reacciones_alergicas', 'otros', e.target.value)
                  })}
                </div>
              </div>
            </div>

            {/* Observaciones importantes */}
            <div className={styles.fichaMedicaSection}>
              <h4 className={styles.fichaMedicaSectionTitle}>Observaciones importantes</h4>
              <textarea
                className={styles.fichaMedicaTextarea}
                value={formData.observaciones_importantes}
                onChange={(e) => handleInputChange('observaciones_importantes', e.target.value)}
                rows="4"
                placeholder={isEditing ? 'Escriba observaciones importantes sobre la salud del estudiante' : ''}
                disabled={!isEditing}
              />
            </div>
          </div>
        )}

        {activeSubTab === 'contacto' && (
          <div className={styles.fichaMedicaContacto}>
            {/* Datos del medico */}
            <div className={styles.fichaMedicaSection}>
              <h4 className={styles.fichaMedicaSectionTitle}>Datos del medico</h4>
              <div className={styles.fichaMedicaContactoGrid}>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Nombre del medico</label>
                  {renderInput({
                    value: formData.medico.nombre,
                    onChange: (e) => handleNestedChange('medico', 'nombre', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Domicilio</label>
                  {renderInput({
                    value: formData.medico.domicilio,
                    onChange: (e) => handleNestedChange('medico', 'domicilio', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Telefonos</label>
                  {renderInput({
                    value: formData.medico.telefonos,
                    onChange: (e) => handleNestedChange('medico', 'telefonos', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Clinica de preferencia</label>
                  {renderInput({
                    value: formData.medico.clinica_preferencia,
                    onChange: (e) => handleNestedChange('medico', 'clinica_preferencia', e.target.value)
                  })}
                </div>
              </div>
            </div>

            {/* En caso de emergencia contactar a */}
            <div className={styles.fichaMedicaSection}>
              <h4 className={styles.fichaMedicaSectionTitle}>En caso de emergencia contactar a:</h4>

              {/* Papa */}
              <div className={styles.fichaMedicaEmergenciaRow}>
                <div className={styles.fichaMedicaEmergenciaLabel}>Papa</div>
                <div className={styles.fichaMedicaField}>
                  {renderInput({
                    value: formData.emergencia_papa.nombre,
                    onChange: (e) => handleNestedChange('emergencia_papa', 'nombre', e.target.value),
                    placeholder: 'Nombre completo'
                  })}
                </div>
                <div className={styles.fichaMedicaEmergenciaLabel}>Telefono</div>
                <div className={styles.fichaMedicaField}>
                  {renderInput({
                    value: formData.emergencia_papa.telefono,
                    onChange: (e) => handleNestedChange('emergencia_papa', 'telefono', e.target.value),
                    placeholder: 'Telefono'
                  })}
                </div>
              </div>

              {/* Mama */}
              <div className={styles.fichaMedicaEmergenciaRow}>
                <div className={styles.fichaMedicaEmergenciaLabel}>Mama</div>
                <div className={styles.fichaMedicaField}>
                  {renderInput({
                    value: formData.emergencia_mama.nombre,
                    onChange: (e) => handleNestedChange('emergencia_mama', 'nombre', e.target.value),
                    placeholder: 'Nombre completo'
                  })}
                </div>
                <div className={styles.fichaMedicaEmergenciaLabel}>Telefono</div>
                <div className={styles.fichaMedicaField}>
                  {renderInput({
                    value: formData.emergencia_mama.telefono,
                    onChange: (e) => handleNestedChange('emergencia_mama', 'telefono', e.target.value),
                    placeholder: 'Telefono'
                  })}
                </div>
              </div>

              {/* Otro */}
              <div className={styles.fichaMedicaEmergenciaRow}>
                <div className={styles.fichaMedicaEmergenciaLabel}>Otro:</div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Nombre</label>
                  {renderInput({
                    value: formData.emergencia_otro.nombre,
                    onChange: (e) => handleNestedChange('emergencia_otro', 'nombre', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Telefono</label>
                  {renderInput({
                    value: formData.emergencia_otro.telefono,
                    onChange: (e) => handleNestedChange('emergencia_otro', 'telefono', e.target.value)
                  })}
                </div>
                <div className={styles.fichaMedicaField}>
                  <label className={styles.fichaMedicaLabel}>Parentesco</label>
                  {renderInput({
                    value: formData.emergencia_otro.parentesco,
                    onChange: (e) => handleNestedChange('emergencia_otro', 'parentesco', e.target.value)
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Botones de accion - Solo visibles en modo edicion */}
      {isEditing && (
        <div className={styles.fichaMedicaActions}>
          <button
            className={styles.fichaMedicaButtonSave}
            onClick={handleSave}
            disabled={saving}
            title="Guardar"
          >
            {saving ? (
              <span style={{ fontSize: 13 }}>Guardando...</span>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
              </svg>
            )}
          </button>
          <button
            className={styles.fichaMedicaButtonCancel}
            onClick={handleCancel}
            disabled={saving}
            title="Cancelar"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Nota al pie */}
      <div className={styles.fichaMedicaFooter}>
        {hasChanges && isEditing && (
          <span style={{ color: '#e67e22', fontWeight: 500 }}>Hay cambios sin guardar</span>
        )}
        {!isEditing && !hasChanges && (
          <span>Datos medicos del estudiante</span>
        )}
      </div>
    </div>
  );
});

export default FichaMedica;
